import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import { Store } from '@ngrx/store';
import * as turf from '@turf/turf';
import { Feature } from 'geojson';
import { cloneDeep, isEmpty } from 'lodash';
import * as mapboxgl from 'mapbox-gl';
import { LngLat } from 'mapbox-gl';
import { Observable } from 'rxjs';

import { ToastLevel } from 'src/app/shared/models/toast-message.module';
import { ToastService } from 'src/app/shared/services/toast.service';
import { SpinnerService } from 'src/app/shared/spinner/spinner.service';
import { initialState, MapControlState, MapDisplayTypeEnum } from 'src/app/store/map-control/map-control.reducer';

import { DRAW_STYLE } from './draw-style';

import { environment } from '../../../../environments/environment';
import { GisService } from '../../../core/api/gis.service';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { MapService } from '../../../core/helper/map.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Buyer, Parcel, Site, Stand, User } from '../../../shared/models';
import * as fromApp from '../../../store';
import { selectAppConfigState } from '../../../store/app-config/app-config.selectors';
import { setMapControlDrawMode, setMapControlsDrawModeOff, toggleMapControlsEditMode, toggleMapControlsKMLUpload, toggleMapControlsStands, } from '../../../store/map-control/map-control.actions';
import { selectMapControlState } from '../../../store/map-control/map-control.selectors';
import { addSelectedStand, deleteSelectedStand, setForestryKml } from '../../../store/parcel/parcel.actions';
import { selectSelectedStands } from '../../../store/parcel/parcel.selectors';
import { MapEvent } from '../map-layers-pages/map-layers-page.component';
import { DialogSelectLayerComponent } from "../../../shared/dialogs/dialog-select-layer/dialog-select-layer.component";

@Component({
  selector: 'fc-map-layers',
  templateUrl: './map-layers.component.html',
  styleUrls: ['./map-layers.component.scss'],
})
export class MapLayersComponent implements OnChanges, OnDestroy {
  @ViewChild('mapCanvas', { static: false }) mapElement: ElementRef;
  @Input() drawer: MatDrawer;
  @Input() mapEvents: Observable<MapEvent>;
  @Input() standListHoverEvents: Observable<{
    isHovered: boolean;
    standId: string;
  }>;
  @Input() regionId: number;
  @Input() site: Site = null;
  @Input() currentUser: User = null;
  @Input() readOnlyMap: boolean = null;
  @Input() preferredBuyer: Buyer = null;
  @Input() parcels: Array<Parcel>;
  @Input() forestryKml: Array<any> = [];
  @Input() approvalRequired: boolean = false;
  @Input() redflagDetails: any[] = [];

  @Output() mapInitialize: EventEmitter<any> = new EventEmitter<any>();
  @Output() generateMap: EventEmitter<any> = new EventEmitter();

  destroyRef = inject(DestroyRef);

  map: mapboxgl.Map;
  style = environment.mapbox.baseMap;

  currentMapControls: MapControlState = initialState;

  draw: any;
  myCustomStands: any[];
  standPopup: mapboxgl.Popup;

  mapStyleLoading = false;
  currentMapStyle: MapDisplayTypeEnum = MapDisplayTypeEnum.SATELLITE;

  parcelIds: Array<number> = [];
  originalBoundingBox: any;
  isMapInitialized = false;
  showZoomInNote = false;

  currentStands: Stand[] = [];
  currentLat: any;
  currentLong: any;
  hoverRedflagFeature = [];
  hoverForestFeature = [];
  redflagMap = [];

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly gisService: GisService,
    public formatHelper: FormatHelperService,
    public siteHelper: SiteHelperService,
    public spinnerService: SpinnerService,
    public dialog: MatDialog,
    readonly http: HttpClient,
    readonly mapService: MapService,
    readonly toastService: ToastService
  ) {
    this.store
      .select(selectAppConfigState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(appConfig => {
        if (typeof appConfig.config['redflag_map'] !== 'undefined') {
          this.redflagMap = JSON.parse(appConfig.config['redflag_map']);
        }
      });
  }

  lockedMapPopup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
  });

  handleLockedMap() {
    this.lockedMapPopup.remove();
    this.map.off('mousemove', this.addLockedMapHover);
    this.store.dispatch(setMapControlDrawMode({ active: true }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.approvalRequired?.currentValue !== changes.approvalRequired?.previousValue) {
      if (!changes.approvalRequired.currentValue && this.isMapInitialized) {
        this.handleLockedMap();
      }
    }
    if (changes.parcels?.currentValue) {
      this.parcels = changes.parcels.currentValue;
      this.parcelIds = this.parcels.map(item => item.gid);
    }
    if (changes.regionId?.currentValue) {
      this.regionId = changes.regionId.currentValue;
    }
    if (changes.forestryKml?.currentValue && this.isMapInitialized) {
      this.forestryKml = changes.forestryKml.currentValue;
      this.buildKMLLayer();
    }
    if (!this.isMapInitialized && !isEmpty(this.parcels) && this.regionId > 0) {
      this.isMapInitialized = true;

      setTimeout(() => {
        const parcelFeatures = this.parcels.map(parcel => parcel.feature);
        this.originalBoundingBox = turf.bbox({
          type: 'FeatureCollection',
          features: parcelFeatures,
        });
        this.initializeMap(this.originalBoundingBox);

        if (this.approvalRequired) {
          this.handleLockedMap();
        }

        this.disableFeatures();
        this.buildMap();
        this.mapEvents
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((event: MapEvent) => this.handleParentEvent(event));
        this.standListHoverEvents
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((event: { isHovered; standId }) => {
            this.handleParentStandListHoverEvent(event);
          });
      }, 0);
    }
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }
  }

  buildFilter(parcels: Parcel[]): string[] {
    return parcels.reduce(
      (polygon, parcel) => {
        polygon.push(String(parcel.llUuid));
        return polygon;
      },
      ['in', 'llUuid']
    );
  }

  handleParentEvent(mapEvent: MapEvent): void {
    if (mapEvent.action === 'delete') {
      this.draw.delete(mapEvent.event.feature.id);
      this.store.dispatch(deleteSelectedStand({ data: { feature: mapEvent.event.feature } }));
    } else if (mapEvent.action === 'center') {
      this.mapService.flyToBoundingBox(mapEvent.event.feature);
    } else {
      console.log('Unknown parent event, ignoring:', mapEvent.action);
    }
  }

  handleParentStandListHoverEvent(event: { isHovered: boolean; standId: string }): void {
    if (event.standId && this.draw && this.draw.get(event.standId)) {
      if (event.isHovered) {
        this.draw.setFeatureProperty(event.standId, 'hover', 1);
      } else {
        this.draw.setFeatureProperty(event.standId, 'hover', 0);
      }

      const feat = this.draw.get(event.standId);
      if (feat && this.currentMapControls.displayStands) {
        this.draw.add(feat);
      }
    }
  }

  initializeMap(boundingBox) {
    (mapboxgl as any).accessToken = environment.mapbox.accessToken;

    this.map = new mapboxgl.Map({
      container: 'forest-map',
      style: this.style,
      bounds: boundingBox,
      preserveDrawingBuffer: true,
    });
    this.mapService.currentMap = this.map;
    this.map.addControl(new mapboxgl.ScaleControl({ maxWidth: 300, unit: 'imperial' }), 'bottom-right');
    this.mapInitialize.emit({
      map: this.map,
      originalBoundingBox: this.originalBoundingBox,
    });
    this.showZoomInNote = this.map.getZoom() < 12.0;
  }

  disableFeatures(): void {
    // Disable default box zooming.
    this.map.boxZoom.disable();

    // disable map rotation using right click + drag
    this.map.dragRotate.disable();

    // disable map rotation using touch rotation gesture
    this.map.touchZoomRotate.disableRotation();

    // Disable default drag zooming when the shift key is held down.
    this.map.dragPan.disable();

    // Disable zoom, pan pitch with keyboard
    this.map.keyboard.disable();

    this.map.doubleClickZoom.disable();
  }

  clearHighlights(): void {
    if (this.map) {
      if (this.map.getLayer('cropped-forest-data-highlighted')) {
        this.map.removeLayer('cropped-forest-data-highlighted');
      }
    }
  }

  buildKMLLayer() {
    if (this.forestryKml && this.forestryKml.length > 0) {
      this.spinnerService.show('app-spinner', true, 'Importing KML');
      try {
        const newCollection = turf.featureCollection(this.forestryKml);
        const flattened = turf.flatten(newCollection);
        const union = flattened.features.reduce((acc, feature) => {
          return turf.union(acc, feature);
        });
        (this.map.getSource('kml-import') as mapboxgl.GeoJSONSource).setData(
          turf.truncate(union.geometry, { coordinates: 2 })
        );
        if (!this.currentMapControls.displayKMLUpload) {
          this.store.dispatch(toggleMapControlsKMLUpload());
        }
        this.gisService
          .getCustomAreaValuationOfStand(this.regionId, union.geometry, this.site.id, this.currentStands)
          .subscribe(standArray => {
            if (standArray.results.length > 0) {
              this.replaceAllStands(standArray.results);
            } else {
              this.toastService.sendMessage('No Stands found for given KML/KMZ', ToastLevel.WARN);
            }
            this.store.dispatch(setForestryKml({ data: [] }));
            this.spinnerService.hide('app-spinner');
          });
      } catch (e) {
        this.toastService.sendMessage('Error getting stands from provided KML/KMZ file', ToastLevel.WARN);
        this.spinnerService.hide('app-spinner');
      }
    }
  }

  addLockedMapHover = e => {
    this.lockedMapPopup
      .setLngLat(e.lngLat)
      .setHTML('<p class="map-popup">In order to change your site, please reject the pending changes first.</p>') // Customize your content
      .addTo(this.map);
  };

  buildMap() {
    if (this.currentUser.isLimitedAdmin || this.currentUser.isAdmin) {
      this.map.on('mousemove', 'public-land-data', e => { this.hoverRedflagFeature = e.features; });
      this.map.on('mouseleave', 'public-land-data', () => { this.hoverRedflagFeature = []; });

      this.map.on('mousemove', 'forest-data', e => {
        if (this.currentMapControls.suggestedStandsSelector) {
          this.hoverForestFeature = e.features;
        }
      });
      this.map.on('mouseleave', 'forest-data', () => { this.hoverForestFeature = []; });
    }
    // Load an image to use as the pattern from an external URL.
    this.mapService.loadImages();

    this.map.on('move', () => {
      let center = this.map.getCenter();
      this.currentLat = center.lat.toFixed(6);
      this.currentLong = center.lng.toFixed(6);
    });
    this.map.on('load', () => {
      this.map.addSource(`kml-import`, {
        type: 'geojson',
        data: null,
      });

      this.map.addLayer({
        id: `kml-import`,
        type: 'fill',
        source: `kml-import`,
        layout: {
          visibility: 'visible',
        },
        paint: {
          'fill-color': 'rgba(27, 229, 204, 0.3)', // '#6e599f',
        },
      });
      this.map.addLayer({
        id: `kml-import-outline`,
        type: 'line',
        source: `kml-import`,
        paint: {
          'line-color': 'rgba(27, 229, 204, 0.7)',
          'line-width': 2,
        },
      });

      const featureCollection: Feature[] = this.parcels.map(s => s.feature);
      this.map.addSource('selected-parcels', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [...featureCollection],
        },
      });
      this.map.addSource('land', {
        type: 'vector',
        tiles: [environment.tileServerUrl + '/maps/layers/{z}/{x}/{y}.vector.pbf'],
        tolerance: 0,
      } as mapboxgl.VectorSource);

      this.map.addLayer({
        id: 'waterways-data1',
        type: 'fill',
        source: 'land',
        'source-layer': 'waterway',
        paint: {
          'fill-antialias': false,
          'fill-color': '#2D5FF5',
          'fill-opacity': 0.5,
        },
        filter: ['==', '$type', 'Polygon'],
        minzoom: 12,
      });

      this.map.addLayer({
        id: 'waterways-data2',
        type: 'line',
        source: 'land',
        'source-layer': 'waterway',
        paint: {
          'line-color': '#2D5FF5',
          'line-width': 2,
        },
        filter: ['==', '$type', 'LineString'],
        minzoom: 12,
      });

      this.map.addLayer({
        id: 'forest-data',
        type: 'fill',
        source: 'land',
        'source-layer': 'forest',
        paint: {
          'fill-outline-color': 'rgba(72,210,7,0.0)',
          'fill-color': '#48D207',
          'fill-opacity': 0.5,
        },
      });

      this.map.addLayer(
        {
          id: 'forest-data-highlighted',
          type: 'fill',
          source: 'land',
          'source-layer': 'forest',
          paint: {
            'fill-outline-color': '#4A90E2',
            'fill-color': 'rgb(63, 121, 174)', // '#6e599f',
            'fill-opacity': 0.75,
          },
          filter: ['in', '$id', ''],
        }
        // 'settlement-label'
      ); // Place polygon under these labels.

      this.map.addLayer({
        id: 'forest-data-outline',
        type: 'line',
        source: 'land',
        'source-layer': 'forest',
        paint: {
          'line-width': 1.15,
          'line-color': 'rgba(72,210,7,0.0)',
        },
      });
      this.map.addLayer({
        id: 'parcel-data-outline2',
        type: 'line',
        source: 'selected-parcels',
        paint: {
          'line-color': 'rgb(255,255,255)',
          'line-dasharray': [2, 2],
          'line-width': 4,
        },
      });

      this.map.addLayer({
        id: 'public-land-data',
        type: 'fill',
        source: 'land',
        'source-layer': 'public_land',
        paint: {
          'fill-outline-color': 'white',
          'fill-color': '#E35542',
          'fill-opacity': 0.5,
        },
      });

      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        userProperties: true,
        controls: {
          trash: true,
        },
        modes: {
          ...MapboxDraw.modes,
          static: StaticMode,
          // simple_select: NewSimpleSelect,
          // direct_select: NewDirectSelect
        },
        styles: DRAW_STYLE,
      });
      this.map.addControl(this.draw, 'top-right');

      // Add map controls
      this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');

      this.addMapListeners();

      // Load selected stands from state
      this.store
        .select(selectSelectedStands)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(stands => {
          this.draw.deleteAll();
          if (stands) {
            this.currentStands = stands;
            stands.map(stand => {
              const clonedStand = cloneDeep(stand);
              clonedStand.id = stand.customId;
              if (this.currentMapControls.displayStands) {
                this.draw.add(clonedStand.feature);
              }
            });
          }
          // reset the draw mode to keep the map from quietly changing it
          if (this.draw) {
            this.draw.changeMode(this.draw.getMode());
          }
        });

      this.buildKMLLayer();

      this.map.fitBounds(this.originalBoundingBox, {
        padding: 75,
        animate: false,
      });

      this.mapService.setLayerVisibility(this.currentMapControls);
      this.map.on('styledata', () => {
        this.mapStyleLoading = false;

        // Handle Draw Mode Toggle
        if (this.approvalRequired) {
          this.map.getCanvas().style.cursor = 'not-allowed';
          this.draw.changeMode('static');
          this.map.on('mousemove', this.addLockedMapHover);
          this.map.getCanvasContainer().addEventListener('mouseleave', e => {
            this.lockedMapPopup.remove();
          });
        } else if (this.currentMapControls.editMode) {
          this.doEditMode();
        } else if (this.currentMapControls.drawPolygonMode) {
          this.draw.changeMode(this.draw.modes.DRAW_POLYGON);
          if (this.standPopup?.isOpen()) {
            this.standPopup.remove();
          }
          this.clearHighlights();
        } else {
          this.draw.changeMode('static');
        }
      });
    });

    this.map.once('idle', () => {
      this.drawer.toggle().then(() => {});
    });
  }

  doEditMode() {
    if (
      this.draw.getMode() !== this.draw.modes.SIMPLE_SELECT &&
      this.draw.getMode() !== this.draw.modes.DIRECT_SELECT
    ) {
      this.draw.changeMode(this.draw.modes.SIMPLE_SELECT);
    }
    if (this.standPopup?.isOpen()) {
      this.standPopup.remove();
    }
  }

  setLayoutProperties() {
    if (this.currentMapControls.displayStands) {
      this.currentStands.forEach(stand => {
        this.draw.add(stand.feature);
      });
    } else {
      this.currentStands.forEach(stand => {
        this.draw.delete(stand.feature.id);
      });
    }
  }

  addMapListeners(): void {
    this.store
      .select(selectMapControlState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mapControls => {
        this.currentMapControls = mapControls;
        // Handle Stand Selector Mode Toggle
        if (mapControls.standSelector || mapControls.redflagSelect) {
          this.addStandSelectListener(mapControls.redflagSelect ? 'public-land-data' : 'forest-data');
        } else {
          this.removeStandSelectListener();
        }

        // Handle Draw Mode Toggle
        if (mapControls.editMode) {
          this.doEditMode();
        } else if (mapControls.drawPolygonMode) {
          this.draw.changeMode(this.draw.modes.DRAW_POLYGON);
          if (this.standPopup?.isOpen()) {
            this.standPopup.remove();
          }
        } else {
          this.draw.changeMode('static');
        }

        if (!this.mapStyleLoading) {
          this.setLayoutProperties();
        }
      });

    this.map.on('draw.selectionchange', () => {
      const mode = this.draw.getMode();
      const selected = this.draw.getSelectedIds()[0];

      if (selected && mode === 'simple_select') {
        this.draw.changeMode('direct_select', { featureId: selected });
      }
    });

    this.map.on('zoomend', e => {
      this.showZoomInNote = e.target.getZoom() < 12.0;
    });

    this.map.on('draw.create', e => {
      if (e.features && e.features.length > 0) {
        const customPolygon = e.features[0];

        this.gisService
          .getCustomAreaValuationOfStand(this.regionId, customPolygon.geometry, this.site.id, this.currentStands)
          .subscribe(
            standArray => {
              if (standArray.error) {
                this.draw.changeMode(this.draw.modes.DIRECT_SELECT, {
                  featureId: customPolygon.id,
                });
                this.store.dispatch(toggleMapControlsEditMode());

                this.dialog.open(DialogYesNoComponent, {
                  panelClass: 'fc-yes-no-dialog',
                  data: {
                    title: 'LINES CANNOT OVERLAP',
                    line1: 'Looks like the line you have just drawn overlaps with another line.  This is not permitted',
                    line2: 'Please adjust the line so that it no longer overlaps another part of the shape',
                    buttonTextYes: 'OK',
                  },
                  autoFocus: false,
                });
              } else if (standArray.results && standArray.results.length === 0) {
                this.draw.delete(customPolygon.id);
                this.draw.changeMode(this.draw.modes.DRAW_POLYGON);

                const lng: number = e.features[0].geometry.coordinates[0][0][0];
                const lat: number = e.features[0].geometry.coordinates[0][0][1];

                const lngLat = { lng, lat };

                let html = '<div class="map-popup">';
                html += '<div class="popup-title">Invalid Selection</div>';
                html += '<div class="popup-border"></div>';
                html +=
                  '<p class="popup-details">If you would like to add a stand to your Carbon Site, it must be at least an acre in size and within your property boundary.</p>';
                html += '</div>';

                this.standPopup = new mapboxgl.Popup({ anchor: 'top' }).setLngLat(lngLat).setHTML(html)
                  .addTo(this.map);
              } else {
                let lngLat = null;
                this.myCustomStands = [];
                let minValuation = 0,
                  maxValuation = 0,
                  totalArea = 0;
                for (const stand of standArray.results) {
                  minValuation += Math.min(...stand.pricingData.annual_valuation.map(obj => obj.valuation));
                  maxValuation += Math.max(...stand.pricingData.annual_valuation.map(obj => obj.valuation));
                  totalArea += stand.area;
                  if (lngLat === null) {
                    const centerCoords = turf.centroid({
                      features: [stand.feature],
                      type: 'FeatureCollection',
                    });
                    lngLat = {
                      lng: centerCoords.geometry.coordinates[0],
                      lat: centerCoords.geometry.coordinates[1],
                    };
                  }
                  this.draw.delete(customPolygon.id);
                  const clonedStand = cloneDeep(stand);
                  this.myCustomStands.push(clonedStand);
                  clonedStand.id = stand.customId;
                  this.draw.add(clonedStand.feature);
                  this.draw.changeMode(this.draw.modes.DRAW_POLYGON);
                }
                this.standPopup = new mapboxgl.Popup({ anchor: 'top' })
                  .setLngLat(lngLat)
                  .setHTML(this.clickedStand(totalArea, [minValuation, maxValuation]))
                  .addTo(this.map)
                  .setMaxWidth('none');

                const addStandElement = document.getElementById('btn-add-stand');
                addStandElement.addEventListener('click', this.onAddCustomStand);

                const removeStandElement = document.getElementById('btn-remove-stand');
                removeStandElement.addEventListener('click', this.onRemoveCustomStand);
                this.standPopup.on('close', () => {
                  addStandElement.removeEventListener('click', this.onAddCustomStand);
                  removeStandElement.removeEventListener('click', this.onRemoveCustomStand);
                  if (this.myCustomStands) {
                    for (const stand of this.myCustomStands) {
                      this.draw.delete(stand.id);
                    }
                    this.myCustomStands = [];
                  }
                  this.draw.changeMode(this.draw.modes.DRAW_POLYGON);
                  if (!this.currentMapControls.displayStands) {
                    this.store.dispatch(toggleMapControlsStands());
                  }
                });
              }
            },
            () => {
              this.draw.delete(customPolygon.id);
            }
          );
      }
    });

    this.map.on('draw.update', e => this.updatePolygon(e.features));
    this.map.on('draw.delete', e => this.deletePolygon(e.features));

    this.map.getCanvas().addEventListener('keyup', this.onKeyboardEvent);
  }

  onKeyboardEvent = event => {
    switch (event.key) {
      case 'Escape':
        // This will exit draw mode if escape is pressed.  Need to do it after default escape key so existing polygon is destroyed
        setTimeout(() => {
          this.store.dispatch(setMapControlsDrawModeOff());
        }, 0);
        break;
    }
  };

  deletePolygon(features): void {
    if (features && features.length > 0) {
      const geojson = features[0];

      // Remove selection highlighting after adding a stand
      this.clearHighlights();

      this.store.dispatch(deleteSelectedStand({ data: { feature: geojson } }));
    }
  }

  updatePolygon(features): void {
    if (features && features.length > 0) {
      const customPolygon = features[0];

      this.gisService
        .getCustomAreaValuationOfStand(this.regionId, customPolygon.geometry, this.site.id, this.currentStands)
        .subscribe(
          standArray => {
            if (standArray.error) {
              this.dialog.open(DialogYesNoComponent, {
                panelClass: 'fc-yes-no-dialog',
                data: {
                  title: 'INVALID POLYGON',
                  line1: 'Error: ' + standArray.error,
                  line2: "Confirm that the polygon doesn't have any overlapping lines",
                  buttonTextYes: 'OK',
                },
                autoFocus: false,
              });
            } else {
              this.store.dispatch(deleteSelectedStand({ data: { feature: customPolygon } }));

              if (standArray.results && standArray.results.length === 0) {
                this.draw.delete(customPolygon.id);
                this.draw.changeMode(this.draw.modes.DRAW_POLYGON);
                this.dialog.open(DialogYesNoComponent, {
                  panelClass: 'fc-yes-no-dialog',
                  data: {
                    title: 'STANDS MUST BE AT LEAST AN ACRE',
                    line1:
                      'If you would like to add a section of property to your Carbon Site, it must be at least an acre in size and within your property boundary.',
                    buttonTextYes: 'OK',
                  },
                  autoFocus: false,
                });
              } else {
                for (const stand of standArray.results) {
                  const clonedStand = cloneDeep(stand);
                  clonedStand.id = stand.customId;
                  clonedStand.feature.id = customPolygon.id;
                  this.addStandToStore(clonedStand);
                  if (this.currentMapControls.displayStands) {
                    this.draw.add(clonedStand.feature);
                  }
                }
              }
            }
          },
          error => {
            console.log('ERROR!', error);
            this.draw.delete(customPolygon.id);
            this.store.dispatch(deleteSelectedStand({ data: { feature: customPolygon } }));
          }
        );
    }
  }

  removeStandSelectListener(): void {
    this.map.off('click', this.onClickStand);
    this.map.off('touchend', this.onClickStand);
    this.map.off('mouseenter', 'forest-data', this.onEnterForestLayer);
    this.map.off('mouseenter', 'public-land-data', this.onEnterForestLayer);
    this.map.off('mouseleave', 'forest-data', this.onLeaveForestLayer);
    this.map.off('mouseleave', 'public-land-data', this.onLeaveForestLayer);
    this.map.getCanvas().style.cursor = '';
  }

  addStandSelectListener(layer: string): void {
    this.map.on('click', this.onClickStand);
    this.map.on('touchend', this.onClickStand);
    this.map.on('mouseenter', layer, this.onEnterForestLayer);
    this.map.on('mouseleave', layer, this.onLeaveForestLayer);
  }

  onLeaveForestLayer = () => {
    this.map.getCanvas().style.cursor = 'not-allowed';
  };

  onEnterForestLayer = () => {
    this.map.getCanvas().style.cursor = 'pointer';
  };

  onClickStand = e => {
    this.clearHighlights();
    // Get click coordinates and current feature
    const point: mapboxgl.PointLike = [e.point.x, e.point.y];
    const features: Feature[] = this.map.queryRenderedFeatures(point, {
      layers: [this.currentMapControls.redflagSelect ? 'public-land-data' : 'forest-data'],
    })

    if (features && features.length > 0) {
      if (this.currentMapControls.redflagSelect) {
        let layers = Array.from(new Set(features.map(f => f.properties.layer)));
        // prompt user for which layer they want to query
        if (layers.length > 1) {
          this.dialog.open(DialogSelectLayerComponent, {
            panelClass: 'fc-yes-no-dialog',
            data: {
              options: layers
            },
            autoFocus: false,
          }).afterClosed().subscribe(result => {
            if (result) {
              this.cropAreaByPoint(e.lngLat, result);
            }
          });
        } else {
          this.cropAreaByPoint(e.lngLat, layers[0]);
        }
      } else {
        this.cropAreaByPoint(e.lngLat);
      }
    }
  };

  processCropAreaServiceCall(serviceCall: any, onlyRemove: boolean, popupLocation: LngLat) {
    serviceCall.subscribe((resp: any) => {
      if (resp.results.length > 0) {
        let totalArea = 0;
        let minValuation = 0;
        let maxValuation = 0;
        this.myCustomStands = [];
        let combinedGeom = null;
        resp.results.forEach((s: Stand) => {
          if (combinedGeom == null) {
            combinedGeom = s.feature;
          } else {
            combinedGeom = turf.union(combinedGeom, s.feature);
          }
          minValuation += Math.min(...s.pricingData.annual_valuation.map(obj => obj.valuation));
          maxValuation += Math.max(...s.pricingData.annual_valuation.map(obj => obj.valuation));
          totalArea += s.area;
          const clonedStand = cloneDeep(s);
          this.myCustomStands.push(clonedStand);
        });

        if (this.standPopup?.isOpen()) {
          this.standPopup.remove();
        }
        if (popupLocation == null) {
          const center = turf.center(combinedGeom);
          const [lng, lat] = center.geometry.coordinates;
          popupLocation = new mapboxgl.LngLat(lng, lat);
        }
        this.standPopup = new mapboxgl.Popup({ anchor: 'top' })
          .setLngLat(popupLocation)
          .setHTML(this.clickedStand(totalArea, [minValuation, maxValuation], onlyRemove))
          .addTo(this.map)
          .setMaxWidth('none');

        const removeStandElement = document.getElementById('btn-remove-stand');
        const addStandElement = document.getElementById('btn-add-stand');

        removeStandElement.addEventListener('click', this.onRemoveCustomStand);
        if (!onlyRemove) {
          addStandElement.addEventListener('click', this.onAddCustomStand);
        }

        this.standPopup.on('close', () => {
          this.clearHighlights();
          if (!onlyRemove) {
            addStandElement.removeEventListener('click', this.onAddCustomStand);
          }
          removeStandElement.removeEventListener('click', this.onRemoveCustomStand);
          if (this.myCustomStands) {
            for (const stand of this.myCustomStands) {
              this.draw.delete(stand.id);
            }
            this.myCustomStands = [];
          }
        });

        if (this.map.getSource('cropped-stand')) {
          this.map.removeSource('cropped-stand');
        }

        const featureCollection: Feature[] = resp.results.map(s => s.feature);
        this.map.addSource('cropped-stand', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [...featureCollection],
          },
        });

        this.map.addLayer({
          id: 'cropped-forest-data-highlighted',
          type: 'fill',
          source: 'cropped-stand',
          paint: {
            'fill-outline-color': '#4A90E2',
            'fill-color': 'rgb(63, 121, 174)', // '#6e599f',
            'fill-opacity': 0.75,
          },
        });
      } else {
        // show error message that selected stand has area less than one acre
        if (this.standPopup?.isOpen()) {
          this.standPopup.remove();
        }
        let html = '<div class="map-popup">';
        html += '<div class="popup-title">Invalid Selection</div>';
        html += '<div class="popup-border"></div>';
        html +=
          '<p class="popup-details">If you would like to add a stand to your Carbon Site, it must be at least an acre in size and within your property boundary.</p>';
        html += '</div>';

        this.standPopup = new mapboxgl.Popup({ anchor: 'top' }).setLngLat(popupLocation).setHTML(html).addTo(this.map);

        // Remove selection highlighting after adding a stand
        this.clearHighlights();
      }
    });
  }

  cropAreaByPoint(clickedPoint: LngLat, layer = null) {
    let serviceCall = null;
    if (layer !== null) {
      let currentGeom = this.getCombinedGeom(this.currentStands);
      serviceCall = this.gisService.getReflagOverlapByClickedPoint(
        this.site.id,
        layer,
        currentGeom.geometry,
        clickedPoint.lng,
        clickedPoint.lat
      );
    } else {
      serviceCall =
        this.gisService
          .getAreaValuationAndGeoJsonStandsCroppedByParcel(
            clickedPoint.lng,
            clickedPoint.lat,
            this.regionId,
            this.site.id,
            layer
          );
    }
    this.processCropAreaServiceCall(serviceCall, layer != null, clickedPoint)
  }

  onAddCustomStand = () => {
    if (this.myCustomStands) {
      for (const stand of this.myCustomStands) {
        this.addStandToStore(stand);
      }
      this.myCustomStands = [];
      this.standPopup.remove();
    }
  };

  getCombinedGeom(stands: Stand[]) {
    let combinedGeom = null;
    if (stands.length > 0) {
      combinedGeom = stands[0].feature;
      for (let i = 1; i < stands.length; i++) {
        combinedGeom = turf.union(combinedGeom, stands[i].feature);
      }
    }
    return combinedGeom;
  }

  onRemoveCustomStand = () => {
    if (this.myCustomStands.length > 0 && this.currentStands.length > 0) {
      let customGeom = this.getCombinedGeom(this.myCustomStands);
      this.spinnerService.show('app-spinner', true, 'Removing Stand');
      this.gisService
        .cutAreaFromSelectedStands(
          this.regionId,
          customGeom.geometry,
          this.site.id,
          this.getCombinedGeom(this.currentStands)
        )
        .subscribe({
          next: standArray => {
            this.replaceAllStands(standArray.results);
          },
          error: () => {
            this.spinnerService.hide('app-spinner');
          },
          complete: () => {
            this.spinnerService.hide('app-spinner');
          },
        });

      this.myCustomStands = [];
    }
    this.standPopup.remove();
  };

  addStandToStore(stand: Stand): void {
    // Remove selection highlighting after adding a stand
    this.clearHighlights();

    this.store.dispatch(addSelectedStand({ data: { stand } }));
  }

  clickedStand(standArea: number, valuationRange: number[], onlyRemove: boolean = false): string {
    const valuation = this.formatHelper.formatMinMaxValuation(valuationRange, true);
    let area = 'Unknown';
    if (standArea) {
      area = standArea.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
    }

    let html = '<div class="map-popup">';
    html += '<div>';
    html += '<div class="popup-title">Estimated Annual Value: ';
    html += '<span class="valuation">' + valuation + '</span>';
    html += '<span style="color: #888888"></span></div>';
    html += '</div>';
    html += '<div class="popup-border"></div>';
    html += '<p class="popup-details">Forested Acres: ';
    html += '<span>' + area + '</span>';
    html += '</p>';
    html += '<div class="popup-border"></div>';
    if (!onlyRemove) {
      html +=
        '<p class="popup-disclaimer">*The boundary of your selection may have been modified to remove areas of less than one forested acre and any non-forested areas.</p>';

      if (valuation.includes('*')) {
        html +=
          '<p class="popup-disclaimer">**Multiple buyers are available for this location.<br/> You will be prompted to choose a buyer before finalizing your carbon site.</p>';
      }
    }
    html += '<div class="button-div">';
    html += '<button class="popup-button content remove-stand" id="btn-remove-stand" value="Remove">Remove</button>';
    if (!onlyRemove) {
      html += '<button class="popup-button content add-stand" id="btn-add-stand" value="Add">Add</button>';
    }
    html += '</div></div>';

    return html;
  }

  replaceAllStands(stands: Array<Stand>) {
    this.currentStands.forEach(stand => {
      this.store.dispatch(deleteSelectedStand({ data: { feature: stand.feature } }));
    });
    for (const stand of stands) {
      this.addStandToStore(stand);
      const clonedStand = cloneDeep(stand);
      clonedStand.id = stand.customId;
      if (this.currentMapControls.displayStands) {
        this.draw.add(clonedStand.feature);
      }
    }
  }


  revertChanges() {
    this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Revert Pending Changes',
        line1: 'This will revert all pending changes to your site.',
        line2: 'Do you want to continue?',
        buttonTextYes: 'Yes, Revert',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    }).afterClosed().subscribe(result => {
      if (result) {
        this.replaceAllStands(this.site.versions[0].stands)
      }
    });
  }

  setMapLoading() {
    this.mapStyleLoading = true;
  }

  selectRedflagLayer(layer: string) {
    if (this.currentMapControls.drawPolygonMode) {
      this.store.dispatch(setMapControlsDrawModeOff());
    }
    let currentGeom = this.getCombinedGeom(this.currentStands);
    let serviceCall = this.gisService.getReflagOverlapByClickedPoint(
      this.site.id,
      layer,
      currentGeom.geometry
    );
    const center = turf.center(currentGeom);
    const [lng, lat] = center.geometry.coordinates;
    this.processCropAreaServiceCall(serviceCall, true, new mapboxgl.LngLat(lng, lat));
  }
}
